import { apiRequest, queryClient } from "@/lib/queryClient";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

interface ActiveSyncConnection {
  connectionId: string;
  adapterId: string;
  adapterName: string;
  direction: string;
  startedAt: string | null;
  currentEntityPhase: string | null;
}

interface SyncCompletionSummary {
  adapterName: string;
  adapterId: string;
  direction: string;
  recordsProcessed: number;
  recordsFailed: number;
  // OCT-3878 (Wave E client surfaces): the post-completion banner reads
  // these so the user sees "42 fetched, 24 unchanged" for any adapter that
  // populates them (QB via Apideck did from day one; Wafeq lit up in
  // OCT-3857). Null when the underlying sync_log row has NULL on the
  // column - either because the adapter pre-dates fetched/unchanged or
  // because the row is from a pre-OCT-3857 Wafeq run.
  recordsFetched: number | null;
  recordsUnchanged: number | null;
  hasErrors: boolean;
}

interface SyncActivityContextValue {
  isSyncActive: boolean;
  activeSyncInfo: ActiveSyncConnection | null;
  syncJustCompleted: SyncCompletionSummary | null;
  dismissCompletion: () => void;
  refreshNow: () => Promise<void>;
}

const SyncActivityContext = createContext<SyncActivityContextValue>({
  isSyncActive: false,
  activeSyncInfo: null,
  syncJustCompleted: null,
  dismissCompletion: () => {},
  refreshNow: async () => {},
});

export function useSyncActivity() {
  return useContext(SyncActivityContext);
}

const POLL_ACTIVE_MS = 5_000;
const POLL_IDLE_BASE_MS = 30_000;
const POLL_IDLE_MAX_MS = 120_000;
const COMPLETION_DISPLAY_MS = 5_000;
const POLL_NO_CONNECTIONS_MS = 300_000;

export function SyncActivityProvider({ children }: { children: React.ReactNode; }) {
  const [isSyncActive, setIsSyncActive] = useState(false);
  const [activeSyncInfo, setActiveSyncInfo] = useState<ActiveSyncConnection | null>(null);
  const [syncJustCompleted, setSyncJustCompleted] = useState<SyncCompletionSummary | null>(null);
  const wasActiveRef = useRef(false);
  const lastActiveTimestampRef = useRef<string | null>(null);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const hasConnectionsRef = useRef<boolean | null>(null);
  const idleBackoffRef = useRef(1);

  const invalidateSyncCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/sync/status"] });
    queryClient.invalidateQueries({ queryKey: ["/api/sync/dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["/api/sync/events"] });
    queryClient.invalidateQueries({ queryKey: ["/api/sync/ledger"] });
    queryClient.invalidateQueries({ queryKey: ["/api/sync/jobs"] });
    queryClient.invalidateQueries({ queryKey: ["/api/customers"] });
    queryClient.invalidateQueries({ queryKey: ["/api/vendors"] });
    queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    queryClient.invalidateQueries({ queryKey: ["/api/bills"] });
    queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["/api/integrations/connections"] });
  }, []);

  const dismissCompletion = useCallback(() => {
    setSyncJustCompleted(null);
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    invalidateSyncCaches();
  }, [invalidateSyncCaches]);

  const fetchCompletionSummary = useCallback(async () => {
    try {
      const since = lastActiveTimestampRef.current
        ? new Date(new Date(lastActiveTimestampRef.current).getTime() - 10_000).toISOString()
        : new Date(Date.now() - 60_000).toISOString();
      const res = await apiRequest("GET", `/api/sync/latest-completed?since=${encodeURIComponent(since)}`);
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.found) return null;
      return data as SyncCompletionSummary;
    } catch {
      return null;
    }
  }, []);

  const getNextInterval = useCallback(() => {
    if (wasActiveRef.current) {
      idleBackoffRef.current = 1;
      return POLL_ACTIVE_MS;
    }
    const interval = Math.min(POLL_IDLE_BASE_MS * idleBackoffRef.current, POLL_IDLE_MAX_MS);
    idleBackoffRef.current = Math.min(idleBackoffRef.current * 1.5, POLL_IDLE_MAX_MS / POLL_IDLE_BASE_MS);
    return interval;
  }, []);

  const poll = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const res = await apiRequest("GET", "/api/sync/active");
      if (!res.ok) return;
      const data = await res.json();

      if (!mountedRef.current) return;

      hasConnectionsRef.current = data.hasConnections ?? true;

      if (data.active && data.connections?.length > 0) {
        const conn = data.connections[0];
        setIsSyncActive(true);
        setActiveSyncInfo(conn);
        wasActiveRef.current = true;
        idleBackoffRef.current = 1;
        if (conn.startedAt) {
          lastActiveTimestampRef.current = conn.startedAt;
        }
      } else {
        setIsSyncActive(false);
        setActiveSyncInfo(null);

        if (wasActiveRef.current) {
          wasActiveRef.current = false;
          idleBackoffRef.current = 1;
          const summary = await fetchCompletionSummary();
          if (summary && mountedRef.current) {
            setSyncJustCompleted(summary);
            if (completionTimerRef.current) {
              clearTimeout(completionTimerRef.current);
            }
            completionTimerRef.current = setTimeout(() => {
              if (mountedRef.current) {
                setSyncJustCompleted(null);
                invalidateSyncCaches();
              }
              completionTimerRef.current = null;
            }, COMPLETION_DISPLAY_MS);
          } else {
            invalidateSyncCaches();
          }
        }
      }
    } catch {
      // silently ignore polling errors
    }
  }, [fetchCompletionSummary, invalidateSyncCaches]);

  const scheduleNextPoll = useCallback(() => {
    if (!mountedRef.current) return;
    if (hasConnectionsRef.current === false) {
      pollTimerRef.current = setTimeout(async () => {
        await poll();
        scheduleNextPoll();
      }, POLL_NO_CONNECTIONS_MS);
      return;
    }
    const interval = getNextInterval();
    pollTimerRef.current = setTimeout(async () => {
      await poll();
      scheduleNextPoll();
    }, interval);
  }, [poll, getNextInterval]);

  const refreshNow = useCallback(async () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    idleBackoffRef.current = 1;
    await poll();
    scheduleNextPoll();
  }, [poll, scheduleNextPoll]);

  useEffect(() => {
    mountedRef.current = true;
    poll().then(() => scheduleNextPoll());
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
    };
  }, [poll, scheduleNextPoll]);

  return (
    <SyncActivityContext.Provider
      value={{ isSyncActive, activeSyncInfo, syncJustCompleted, dismissCompletion, refreshNow }}
    >
      {children}
    </SyncActivityContext.Provider>
  );
}
